import React from 'react';
import {
  baseSelectStyles,
  getCurrentSelectedOption,
  getNewSelectionValue,
  redesignStyles,
  SelectInput,
  SelectOption,
} from '../../elements/rselect/ReactSelectUtils';
import SortedSelect from '../../elements/rselect/SortedSelect';
import UnsortedSelect from '../../elements/rselect/UnsortedSelect';
import MentionsBasicEditor from '../../elements/editors/draftjs/MentionsBasicEditor';
import {
  convertTextToMentionsState,
  DEFAULT_MACRO_ENTITY_NAME,
  extractPayloadFromMentionContent,
} from '../../../lib/DraftJSUtils';
import JSONUtils from '../../../lib/JSONUtils';
import UIUtils from '../../../lib/UIUtils';
import { MarkdownContent } from 'new/elements/MarkdownContent';

import { assign, find, get, isEmpty, isEqual, isNil, map, omit } from 'lodash';
import { NEXLA_WEBHOOKS_ENABLED } from '../../../constants/config';
import { SUPPORT_KEY_SINK_FORM_REST } from '../../../constants/supportPageKeys';
import BatchFields from '../../BatchFields';
import {
  Checkbox,
  checkboxClasses,
  FormControlLabel,
  formControlLabelClasses,
  Stack,
  styled,
} from '@mui/material';
import Tooltip from 'js/components/elements/mui/Tooltip';
import DebouncedText from 'js/components/elements/editors/mui/DebouncedText';
import { ReactComponent as InfoIcon } from 'new/components/icons/fi-rr-info.svg';

const HTTP_METHODS = [
  {
    label: 'GET',
    value: 'GET',
  },
  {
    label: 'POST',
    value: 'POST',
  },
  {
    label: 'PUT',
    value: 'PUT',
  },
  {
    label: 'PATCH',
    value: 'PATCH',
  },
  {
    label: 'DELETE',
    value: 'DELETE',
  },
];
const CONTENT_FORMAT = [
  {
    label: 'JSON',
    value: 'application/json',
  },
  {
    label: 'XML',
    value: 'application/xml',
  },
];

const StyledFormControlLabel = styled(FormControlLabel)({
  [`.${formControlLabelClasses.label}`]: {
    fontSize: 12,
    fontWeight: 500,
    color: '#1F2F40',
    lineHeight: '24px',
    textTransform: 'initial',
    letterSpacing: '.5px',
  },
});

const StyledCheckbox = styled(Checkbox)({
  color: '#4AD496 !important',
  [`.${checkboxClasses.checked}`]: {
    color: '#4AD496 !important',
  },
});

const getMentionsFromSchema = (dataSchema) => {
  return map(dataSchema, (attr) => {
    const attrName = UIUtils.getAttrDisplayName(attr);

    return {
      name: `{${attrName}}`,
      display_name: attrName,
      type: 'attr',
      type_display: 'Data Attributes',
    };
  });
};

const getStartingState = (props) => {
  const { dataSchema, sinkConfig } = props;
  const url = (sinkConfig && sinkConfig['url.template']) || '';
  const urlSuggestions = getMentionsFromSchema(dataSchema);
  const urlTemplateEditorState = convertTextToMentionsState(url, urlSuggestions);

  return {
    urlTemplateEditorState,
    urlSuggestions,
    filteredURLSuggestions: urlSuggestions,
  };
};

class RestConfigurationSection extends React.Component {
  constructor(props) {
    super(props);

    this.state = getStartingState(props);
  }

  componentDidMount() {
    if (this.props.setContextualSupport) {
      this.props.setContextualSupport(SUPPORT_KEY_SINK_FORM_REST);
    }
  }

  componentDidUpdate = (prevProps) => {
    if (this.props.dataSchema !== prevProps.dataSchema) {
      const { urlTemplateEditorState, urlSuggestions, filteredURLSuggestions } = getStartingState(
        this.props,
      );

      this.setState({
        urlTemplateEditorState,
        urlSuggestions,
        filteredURLSuggestions,
      });
    }
  };

  componentWillUnmount = () => {
    this.props.onSinkMappingUpdated(null, true);
    if (this.props.unsetContextualSupport) {
      this.props.unsetContextualSupport(SUPPORT_KEY_SINK_FORM_REST);
    }
  };

  _updateSinkConfig = (name, value) => {
    this.props.onSinkConfigChanged(
      {
        ...this.props.sinkConfig,
        [name]: value,
      },
      name,
      value,
    );
  };

  _removeSinkConfig = (keys) => {
    this.props.onSinkConfigChanged(omit(this.props.sinkConfig, keys));
  };

  _updateBatchSinkConfig = (configs) => {
    this.props.onSinkConfigChanged({
      ...this.props.sinkConfig,
      ...configs,
    });
  };

  _saveURLTemplateDebounced = (editorState) => {
    const { macroMap, stageURLMacros } = this.state;
    const contentState = editorState.getCurrentContent();
    const { payloadText } = extractPayloadFromMentionContent(
      contentState,
      DEFAULT_MACRO_ENTITY_NAME,
      true,
      stageURLMacros,
      macroMap,
    );

    this._updateSinkConfig('url.template', payloadText.trim());
  };

  _handleURLTemplateEditorChange = (editorState) => {
    if (editorState.getCurrentContent() !== this.state.urlTemplateEditorState.getCurrentContent()) {
      // content has changed
      this._saveURLTemplateDebounced(editorState);
    }

    this.setState({
      urlTemplateEditorState: editorState,
    });
  };

  _handleURLTemplateEditorSearch = (suggestions) => {
    this.setState({
      filteredURLSuggestions: suggestions,
    });
  };

  render() {
    const {
      paging: { sinkConfigById },
      currentOrg,
      dataSchema,
      addedDataSink,
      onSinkMappingUpdated,
      selectedConnection,
      sinkConfig,
      onSinkConfigChanged,
    } = this.props;
    const shouldCreateDataSource =
      sinkConfig && !isNil(sinkConfig['create.datasource'])
        ? sinkConfig['create.datasource']
        : false;
    const apiMethodSelection = getCurrentSelectedOption(HTTP_METHODS, sinkConfig['method']);
    const sinkConfigFields = sinkConfigById[selectedConnection];
    const hcScaleFactorField = find(sinkConfigFields, {
      name: 'hc_scale_factor',
    });
    const batchModeField = find(sinkConfigFields, {
      name: 'batch.mode',
    });
    const hcScaleFactor =
      (sinkConfig && sinkConfig['hc_scale_factor']) || hcScaleFactorField?.default;
    const hcScaleFactorOptions = hcScaleFactorField
      ? UIUtils.disableInvalidHCOptions(
          hcScaleFactorField.options,
          addedDataSink &&
            addedDataSink.sink_config &&
            addedDataSink.sink_config['hc_scale_factor'],
        )
      : [];

    const selectStyles = {
      ...baseSelectStyles,
      container: (styles) => ({ ...styles, minWidth: 80, zIndex: 9999 }),
    };

    let contentTypeOptions = CONTENT_FORMAT;

    if (!isEmpty(sinkConfig['content.type'])) {
      //is this value one of predefined content formats
      let currentValue = sinkConfig['content.type'];
      let valuePresent = find(contentTypeOptions, (o) => isEqual(o.value, currentValue));
      if (isNil(valuePresent)) {
        contentTypeOptions = [...contentTypeOptions, { label: currentValue, value: currentValue }];
      }
    }

    let contentTypeSelection = getCurrentSelectedOption(
      contentTypeOptions,
      sinkConfig['content.type'],
    );

    const excludesOptions = map(dataSchema, (attr) => {
      const attrName = UIUtils.getAttrDisplayName(attr);

      return {
        label: attrName,
        value: attrName,
      };
    });
    const excludesSelection = map(get(sinkConfig, 'mapping.excludes'), (attrName) =>
      getCurrentSelectedOption(excludesOptions, attrName),
    );

    return (
      <div className="body-section">
        <div className="description">
          Set the API endpoint you wish to write data to. You can also configure Nexla to
          automatically send the API response to a new Nexla Webhook source.
        </div>
        <div>
          <div className="input-row">
            <Stack width="100%" direction="row" alignItems="center" gap={1}>
              <div className="input-holder method-input-holder">
                <label className="sink-config sink-config-rest-method">Method</label>
                <UnsortedSelect
                  id={'method'}
                  value={apiMethodSelection}
                  options={HTTP_METHODS}
                  components={{ Input: SelectInput, Option: SelectOption }}
                  isClearable={false}
                  isSearchable
                  backspaceRemovesValue={false}
                  className="input-type-select"
                  styles={redesignStyles}
                  onChange={(newValue) => {
                    const value = getNewSelectionValue(newValue, true, '');
                    this._updateSinkConfig('method', value);
                  }}
                />
              </div>
              <div className="input-holder method-input-holder">
                <label className="sink-config sink-config-rest-content.type">Content Format</label>
                <UnsortedSelect
                  id={'content.type'}
                  value={contentTypeSelection}
                  components={{ Input: SelectInput, Option: SelectOption }}
                  options={contentTypeOptions}
                  isClearable={false}
                  isSearchable
                  isCreatable
                  backspaceRemovesValue={false}
                  className="input-type-select"
                  styles={redesignStyles}
                  onChange={(newValue) => {
                    const value = getNewSelectionValue(newValue, true, '');
                    this._updateSinkConfig('content.type', value);
                  }}
                />
              </div>
            </Stack>

            <div className="input-holder">
              <label className="sink-config sink-config-rest-url.template">URL</label>
              <div>
                <MentionsBasicEditor
                  style={{ padding: '8px 12px' }}
                  isSingleLine={false}
                  allSuggestions={this.state.urlSuggestions}
                  editorState={this.state.urlTemplateEditorState}
                  suggestions={this.state.filteredURLSuggestions}
                  onChange={this._handleURLTemplateEditorChange}
                  onSearchChange={this._handleURLTemplateEditorSearch}
                  groupByKey={'type'}
                  groupDisplayNameKey={'type_display'}
                  groupByEntryComponent={<div />}
                  editorKey={'url_input'}
                  className="url-template-editor"
                />
              </div>
            </div>

            <div className="input-holder">
              <Tooltip
                position="right"
                tooltipTheme="fieldInfo"
                title={
                  <MarkdownContent>
                    Add any optional request headers that must be sent as part of this request. You
                    do not need to repeat headers already present in credentials. Please input as
                    comma-separated values e.g `header1:value1,header2:value2`
                  </MarkdownContent>
                }
              >
                <label
                  className="sink-config sink-config-rest-request.headers"
                  htmlFor="request.headers"
                >
                  Request Headers
                  <InfoIcon />
                </label>
              </Tooltip>
              <div>
                <DebouncedText
                  id="request.headers"
                  data-testid="test-id-request.headers"
                  value={sinkConfig['request.headers'] || ''}
                  debounceTimeMs={200}
                  multiline={true}
                  minRows={4}
                  maxRows={8}
                  onChange={(text) => {
                    this._updateSinkConfig('request.headers', text);
                  }}
                />
              </div>
            </div>

            <div className="input-holder mapping-excludes-input-holder">
              <label className="sink-config sink-config-rest-mapping.excludes">
                Exclude Attributes
              </label>
              <UnsortedSelect
                id="mapping.excludes"
                value={excludesSelection}
                components={{ Input: SelectInput, Option: SelectOption }}
                options={excludesOptions}
                isClearable
                isSearchable
                isMulti
                backspaceRemovesValue
                styles={redesignStyles}
                onChange={(newValue) => {
                  let value = getNewSelectionValue(newValue, true, '');
                  onSinkMappingUpdated(
                    JSONUtils.stringifyJson(assign({}, sinkConfig.mapping, { excludes: value })),
                    true,
                  );
                }}
              />
            </div>
          </div>

          <BatchFields onSinkConfigChanged={onSinkConfigChanged} sinkConfig={sinkConfig} />

          {NEXLA_WEBHOOKS_ENABLED && (
            <div className="check-box-holder ">
              <StyledFormControlLabel
                label="Would you like to process the API response as a Nexla Webhook source?"
                className="sink-config sink-config-create.datasource"
                control={
                  <StyledCheckbox
                    checked={shouldCreateDataSource}
                    onChange={(event, isChecked) =>
                      this._updateSinkConfig('create.datasource', isChecked)
                    }
                  />
                }
              />
            </div>
          )}
          {UIUtils.isHCEnabled(currentOrg.id, selectedConnection, false) && (
            <div className="input-row">
              <div className="input-holder">
                <label className="sink-config">{hcScaleFactorField.display_name}</label>
                <SortedSelect
                  value={getCurrentSelectedOption(hcScaleFactorOptions, hcScaleFactor)}
                  components={{
                    Input: SelectInput,
                    Option: SelectOption,
                  }}
                  options={hcScaleFactorOptions}
                  isClearable={false}
                  backspaceRemovesValue={false}
                  isSearchable
                  styles={selectStyles}
                  onChange={(newValue) => {
                    const value = getNewSelectionValue(newValue, true);
                    this._updateSinkConfig(hcScaleFactorField.name, value);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default RestConfigurationSection;
